// Licensed Materials - Property of IBM
//
// IBM Watson Analytics
//
// (C) Copyright IBM Corp. 2019
//
// US Government Users Restricted Rights - Use, duplication or
// disclosure restricted by GSA ADP Schedule Contract with IBM Corp.

import JSZip from 'jszip';
import StringResources from 'nls/StringResources';

let MB = 1000000;

/**
 * Utility class to load an SVG which can either be a single SVG or a zipped file containing one or more SVG's
 */
export default class FileLoader {

	constructor( _reader, _zipMaxSize, _imgMaxSize, _allowedFileExtensions ) {
		this._files = null;
		if ( _reader )
			this._reader = _reader;

		if( _zipMaxSize )
			this._zipMaxSize = _zipMaxSize;

		if( _imgMaxSize )
			this._imgMaxSize = _imgMaxSize;

		this._allowedFileExtensions = _allowedFileExtensions;
	}

	/**
	 * Load one or more files or a single zip file
	 * @param {Array} _files Array of file names
	 * @param {Function} _callback The function to be called with the SVG file list
	 */
	load = ( _files, _callback, _reject ) => {
		try {
			var results = [];

			// Define the file info if not created
			if ( !this._files )
				this._files = {};

			// Load each file returning a promise for each one
			for( var i = 0; i < _files.length; i++ ) {
				results.push( this._loadFile( _files[ i ] ) );
			}

			// Process all the promises and when all have finished fire the callback
			// function returning the list of svg names that have been loaded
			Promise.all( results ).then( function( _dataArray )	{
				// The result of the load will always return an array for each file
				// even if one file is selected.
				// Merge all arrays into one
				var mergedArrays = [].concat.apply( [], _dataArray );
				return _callback( mergedArrays );
			}.bind( this ), _reject );
		} catch ( _error ) {
			_reject( _error );
		}
	}

	/**
	 * Get the svg contents
	 * @param {String} _fileName The file name
	 * @returns {Promise} Return a promise
	 */
	getFileAsBlob = ( _fileName ) => {
		return new Promise( function( _resolve, _reject ) {
			var _fileData = this._getFileInfo( _fileName );
			if ( _fileData ) {
				if ( _fileData.type === 'zip' ) {
					try {
						var file =
						{
							name: _fileData.data[ _fileName ].name,
							size: _fileData.data[ _fileName ]._data.uncompressedSize
						};
						this._verifyImgFileSize( file );
						_fileData.data[ _fileName ]
							.async( 'blob' ).catch( function( _error ) {
								_reject( _error );
							} ).then( function( _content ) {
								_resolve( _content );
							} );
					} catch( _error ) {
						_reject( _error );
					}
				} else
					_resolve( _fileData.data );
			} else
				_reject( { message: StringResources.get('schematicsInvalidSvgData') } );
		}.bind( this ) );
	}

	/**
	 * List =the SVGs found in a zip file
	 * @param {String} _zip The filename
	 * @returns {Array} List of file names
	 */
	_listZip = ( _zip ) => {
		var files = [];

		Object.keys( _zip ).forEach( function( _fileName ) {
			files.push( _fileName );
		} );

		return files.filter( this._isSVG );
	}

	/**
	 * Load a file
	 * @param {Object} _file The file object
	 * @param {FileReader} _fileReader A file reader object
	 * @returns {Promise} A promise that loads the file
	 */
	_loadFile = ( _file ) => {
		var results = [];
		var fileReader = !this._reader ? new window.FileReader() : this._reader;
		var fileType = _file.name.toLowerCase().substr( -3 );

		if ( this._isSVG( _file.name ) || this._isAllowedFile( _file.name ) ) {
			var data = _file;
			this._verifyImgFileSize( _file );
			this._files[ _file.name ] = this._fileData( _file.name, fileType, data, _file.size );
			results.push( _file.name );
			return Promise.resolve( results );
		} else if ( !this._allowedFileExtensions && this._isZip( _file ) ) {
			return new Promise( function( _resolve, _reject ) {
				fileReader.onload = function( e ) {
					// Zipped svgs
					if ( this._isZip( _file ) ) {
						// Load asynchronously
						JSZip.loadAsync( e.target.result ).then( function( jsZip ) {
							this._files[ _file.name ] = this._fileData( _file.name, 'zip', jsZip.files, _file.size );
							// Get all SVG files in the zip
							var files = this._listZip( jsZip.files );
							for ( var i = 0; i < files.length; i++ ) {
								results.push( files[ i ] );
							}
							_resolve( results );
						}.bind( this ), function( error ) {
							_reject( error );
						} );
					} else {
						var data = _file;
						this._files[ _file.name ] = this._fileData( _file.name, 'svg', data, _file.size );
						results.push( _file.name );
						_resolve( results );
					}
				}.bind( this );

				try {
					this._readFile( _file, fileReader );
				} catch ( e ) {
					_reject( e );
				}

			}.bind( this ) );
		} else {
			throw new Error( StringResources.get('schematicsInvalidType', { type:  _file.name } ) );
		}
	}

	/**
	 * Determine if the file is a zip file
	 * @param {File} _file A file object
	 * @returns {Boolean} true is the file is a zip
	 */
	_isZip = ( _file ) => {
		return ( _file && _file.name &&
			_file.name.toLowerCase().substr( -4 ) === '.zip' ) &&
			( _file.type === 'application/x-zip-compressed' || _file.type === 'application/zip' || _file.type === 'application/x-compressed' );
	}

	_isSVG = ( _fileName ) => {
		return _fileName.toLowerCase().substr( -4 ) ===  '.svg';
	}

	_isAllowedFile = ( _fileName ) => {
		return this._allowedFileExtensions && this._allowedFileExtensions.indexOf( _fileName.toLowerCase().substr( -4 ) ) >= 0;
	}

	/**
	 * Read the file contents
	 * @param {File} _file A file object
	 * @param {FileReader} The file reader
	 */
	_readFile = ( _file, _fileReader ) => {
		if ( this._isZip( _file ) )	{
			this._verifyZipFileSize( _file );
			_fileReader.readAsArrayBuffer( _file );
		} else if( this._isSVG( _file ) ) {
			this._verifyImgFileSize( _file );
			_fileReader.readAsDataURL( _file );
		} else {
			throw Error( StringResources.get('schematicsInvalidType', { type:  _file.name } ) );
		}
	}

	_verifyZipFileSize = ( _file ) => {
		if( _file.size > this._zipMaxSize * MB ) {
			throw Error( StringResources.get('schematicsInvalidSize', { file: _file.name, size: _file.size } ) );
		}
	}

	_verifyImgFileSize = ( _file ) => {
		if( _file.size > this._imgMaxSize * MB ) {
			throw Error( StringResources.get('schematicsInvalidSize', { file : _file.name, size: _file.size } ) );
		}
	}

	/**
	 * Creates a file data object
	 * @param {String} _name
	 * @param {String} _type zip|svg
	 * @param {Object} _data Depending on the type this will be a map of ZipObjects or for single files, the svg text
	 * @returns {Object} The file data object
	 */
	_fileData = ( _name, _type, _data ) => {
		return {
			'name': _name,
			'type': _type,
			'data': _data
		};
	}

	// IE do not support array.find()
	_arrayFind = ( _arr, _obj ) => {
		for ( var idx = 0; idx < _arr.length; ++idx ) {
			if ( _arr[ idx ] === _obj )
				return _obj;
		}
		return null;
	};

	/**
	 * Get file metadata
	 * @param {String} _fileName The file name
	 * @returns {Object} Return the file metadata. See _fileData()
	 */
	_getFileInfo = ( _fileName ) => {
		var keys = Object.keys( this._files );
		var result;

		// Check first if the file is in the list
		var file = this._arrayFind( keys, _fileName );

		if ( file ) {
			result = this._files[ file ];
		} else {
			for( var i = 0; i < keys.length; i++ ) {
				var key = keys[ i ];
				var fileData = this._files[ key ];
				if ( fileData.type === 'zip' ) {
					file = this._arrayFind( Object.keys( fileData.data ), _fileName );
					if ( file ) {
						result = fileData;
						break;
					}
				}
			}
		}
		return result;
	}
}



// WEBPACK FOOTER //
// ./src/js/utilities/FileLoader.js